import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flag, X, AlertTriangle, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportListingModal = ({ isOpen, onClose, listingId, listingTitle, token }) => {
  const [violationTypes, setViolationTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [alreadyReported, setAlreadyReported] = useState(false);

  useEffect(() => {
    if (isOpen && token) {
      // Fetch violation types
      fetch(`${API_URL}/api/reports/violation-types`)
        .then(res => res.json())
        .then(data => setViolationTypes(data.types || []))
        .catch(err => console.error('Failed to fetch violation types:', err));

      // Check if already reported
      fetch(`${API_URL}/api/reports/listing/${listingId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.has_reported) {
            setAlreadyReported(true);
          }
        })
        .catch(err => console.error('Failed to check report status:', err));
    }
  }, [isOpen, listingId, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedType) {
      setError('Please select a violation type');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          listing_id: listingId,
          violation_type: selectedType,
          description: description || null
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit report');
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setSelectedType('');
        setDescription('');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setSuccess(false);
    setError('');
    setSelectedType('');
    setDescription('');
    setAlreadyReported(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Flag className="w-5 h-5 text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-white">Report Listing</h2>
          </div>
          <button
            onClick={resetAndClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Success State */}
          {success && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Report Submitted</h3>
              <p className="text-zinc-400">
                Thank you for helping keep our marketplace safe. We'll review this listing within 24-48 hours.
              </p>
            </div>
          )}

          {/* Already Reported State */}
          {!success && alreadyReported && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Already Reported</h3>
              <p className="text-zinc-400 mb-4">
                You have already submitted a report for this listing. Our team is reviewing it.
              </p>
              <button
                onClick={resetAndClose}
                className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {/* Report Form */}
          {!success && !alreadyReported && (
            <form onSubmit={handleSubmit}>
              {/* Listing Info */}
              <div className="bg-zinc-800/50 rounded-lg p-3 mb-4">
                <p className="text-xs text-zinc-500 mb-1">Reporting:</p>
                <p className="text-white font-medium truncate">{listingTitle}</p>
              </div>

              {/* Rules Link */}
              <div className="flex items-center gap-2 mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <p className="text-sm text-zinc-300">
                  Review our{' '}
                  <Link
                    to="/rules"
                    target="_blank"
                    className="text-amber-400 hover:underline inline-flex items-center gap-1"
                  >
                    Marketplace Rules
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                  {' '}before reporting.
                </p>
              </div>

              {/* Violation Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Violation Type *
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a violation type...</option>
                  {violationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Additional Details (Optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide any additional context that might help our review team..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder:text-zinc-500 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-zinc-500 mt-1 text-right">
                  {description.length}/500
                </p>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !selectedType}
                className="w-full py-3 bg-red-500 hover:bg-red-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Flag className="w-5 h-5" />
                    Submit Report
                  </>
                )}
              </button>

              <p className="text-xs text-zinc-500 text-center mt-3">
                False reports may result in account restrictions.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportListingModal;
